/**
 * fSpy
 * Copyright (c) 2020 - Per Gantelius
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import * as React from 'react'
import ControlPointsContainer from './containers/control-points-container'
import ResultContainer from './containers/result-container'
import SettingsContainer from './containers/settings-container'

import { StoreState } from './types/store-state'
import { connect } from 'react-redux'
import { post } from './http'
import { load, save, openTaylor3d } from './util';
import { AppAction, setImage } from './actions'
import { GlobalSettings } from './types/global-settings'
import { UIState } from './types/ui-state'
import { ImageState } from './types/image-state'
import { SolverResult } from './solver/solver-result'
import { Dispatch } from 'redux'

import { Oval } from  'react-loader-spinner'

interface AppProps {
  uiState: UIState,
  globalSettings: GlobalSettings,
  solverResult: SolverResult,
  image: ImageState,
  onImageLoaded(filePath: string): void,
  onProjectLoaded(filePath: string): void
}

let sceneDataFetched = false;
let visible = false;

class App extends React.PureComponent<AppProps> {
  readonly code = this.querryStrings('code');
  constructor(props: AppProps) {
    super(props);
    this.init();
  }

  querryStrings(key: string): string | null {
    key = key.replace(/[*+?^$.\[\]{}()|\\\/]/g, "\\$&");
    const match = window.location.search.match(new RegExp(`[?&]${key}=([^&]+)(&|$)`));
    return match && decodeURIComponent(match[1].replace(/\+/g, " "));
  }

  init() {
    const auth = this.querryStrings('auth');
    if (auth) {
      localStorage.setItem("authToken", auth);
      let currentLocation = window.location.href;
      currentLocation = currentLocation.split("&auth=")[0];
      window.location.href = currentLocation;
    }
    if (this.code) {
      this.loadSceneData();
    }
  }

  loadSceneData() {
    visible = true;
    let server = 
        (
          window.location.protocol === "https:" &&
          window.location.href.indexOf("dev") === -1
        ) ? 
        "https://server.vs24.com" : "http://server.vs24develop.com";

    if (!sceneDataFetched) {
      sceneDataFetched = true;
      const header = {
        "Content-Type": "application/json",
        Accept: "application/json, text/plain, */*"
      }
      post(`${server}/api/taylor/${this.code}/get_input_data`, header)
        .then((response:Response) => response.json())
        .then(async (response:any) => {
          const rnd = Math.floor(Math.random() * 2500 + 1);
          if (response.fspy) {
            const fspyUrl = `${response.fspy}?rnd=${rnd}`;
            await this.props.onProjectLoaded(fspyUrl);
            await new Promise(resolve => setTimeout(resolve, 200));
          }
          const url = `${server}/api/blend/thumbnail?url=${response.image}&width=1280&rnd=${rnd}`;
          this.props.onImageLoaded(url);
        });
    }
  }

  render() {
    return (
      <div id='app-container'>
        <SettingsContainer isVisible={this.props.uiState.sidePanelsAreVisible} />
        <ControlPointsContainer />
        <ResultContainer isVisible={this.props.uiState.sidePanelsAreVisible} />
        <Oval
          height={80}
          width={80}
          color="lightgray"
          wrapperStyle={{}}
          wrapperClass="center-wrapper"
          visible={visible}
          ariaLabel='oval-loading'
          secondaryColor="gray"
          strokeWidth={2}
          strokeWidthSecondary={2}
        />
        <div style={{ alignSelf: 'center' }}>
          <button
            style={{
              color: '#a0a0a0',
              marginTop: '100px',
              height: '28px',
              width: '150px',
              border: 'none',
              boxShadow: 'none',
              outline: 'none',
              backgroundColor: '#374146',
              position: 'absolute',
              bottom: '5px',
              right: '5px'
            }}
            onClick={() => triggerSave(this.code)}
            disabled={visible || !this.code}>
            Save
          </button>
          <a id="download-link" />
        </div>
      </div>
    )
  }
}

const triggerSave = (code: string | null) => {
  if (visible || !code) {
    return;
  }
  visible = true;
  save(code).then(() => {
    openTaylor3d(code);
    visible = false;
    const elems = document.getElementsByClassName("center-wrapper") as HTMLCollectionOf<HTMLDivElement>;
    for (let i = 0; i < elems.length; i++) {
      elems[i].style.display = "none";
    }
  });
}

const loadBackgroundImage = (file: string, dispatch: Dispatch<AppAction>) => {
    const xhr = new XMLHttpRequest();
    xhr.open("GET", file, true);
    xhr.responseType = "arraybuffer";

    xhr.onload = function(e) {
      const arrayBufferView = new Uint8Array(this.response);
      const blob = new Blob([arrayBufferView]);
      const urlCreator = window.URL || window.webkitURL;
      const imageUrl = urlCreator.createObjectURL(blob);
      const image = document.createElement("img");
      image.src = imageUrl;
      image.onload = (event) => {
        const img = event.target as HTMLImageElement;
        dispatch(setImage(file, arrayBufferView as Buffer, img.width, img.height));
        visible = false;
      }
    };

    xhr.send();
};
  
export function mapStateToProps(state: StoreState) {
  return {
    uiState: state.uiState,
    globalSettings: state.globalSettings,
    solverResult: state.solverResult,
    image: state.image
  }
}

export function mapDispatchToProps(dispatch: Dispatch<AppAction>) {
  return {
    onImageLoaded: (url: string) => {
      loadBackgroundImage(url, dispatch);      
    },
    onProjectLoaded: (url: string) => {
      load(url, dispatch).then(() => visible = false);
    },
  }
}


export default connect(mapStateToProps, mapDispatchToProps)(App)
